import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";

export default function EventGallery() {
  const [items, setItems] = useState(null); // null=loading, []=empty
  const [lightbox, setLightbox] = useState(null); // index
  const trackRef = useRef(null);
  const autoRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const visible = (items || []).slice(0, 12);

  // autoplay (stops on interaction / lightbox)
  useEffect(() => {
    if (!visible.length) return;
    const tick = () => {
      if (pausedRef.current || lightbox !== null) return;
      const el = trackRef.current;
      if (!el) return;
      const card = el.querySelector("[data-gallery-card]");
      const step = card ? card.offsetWidth + 16 : 320;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 8) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: step, behavior: "smooth" });
    };
    autoRef.current = setInterval(tick, 3500);
    return () => clearInterval(autoRef.current);
  }, [visible.length, lightbox]);

  const pause = () => { pausedRef.current = true; };
  const scrollBy = (dir) => {
    pause();
    const el = trackRef.current;
    const card = el?.querySelector("[data-gallery-card]");
    const step = card ? card.offsetWidth + 16 : 320;
    el?.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const onKey = useCallback((e) => {
    if (lightbox === null) return;
    if (e.key === "Escape") setLightbox(null);
    if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % visible.length);
    if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + visible.length) % visible.length);
  }, [lightbox, visible.length]);

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  // Loading skeleton (locked 4:3, no layout shift)
  if (items === null) {
    return (
      <section className="ev-section" style={{ paddingTop: 0 }} data-testid="ev-gallery-skeleton">
        <p className="ev-section-eyebrow">Recent events</p>
        <h2>From our kitchen to your table</h2>
        <div className="ev-gallery-track" style={{ overflow: "hidden" }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} data-gallery-card className="ev-gallery-card" style={{ background: "var(--surface-2)" }} />
          ))}
        </div>
      </section>
    );
  }

  // Empty → hide the whole section (no error, no placeholder)
  if (!visible.length) return null;

  return (
    <section className="ev-section" style={{ paddingTop: 0 }} data-testid="ev-gallery">
      <p className="ev-section-eyebrow">Recent events</p>
      <h2>From our kitchen to your table</h2>
      <div className="ev-gallery-wrap">
        <button className="ev-gallery-arrow left" onClick={() => scrollBy(-1)} aria-label="Previous" data-testid="ev-gallery-prev"><ChevronLeft size={20} /></button>
        <div
          className="ev-gallery-track hide-scroll"
          ref={trackRef}
          onPointerDown={pause}
          onWheel={pause}
          onTouchStart={pause}
          data-testid="ev-gallery-track"
        >
          {visible.map((g, i) => (
            <button
              key={g.id}
              data-gallery-card
              data-testid={`ev-gallery-item-${i}`}
              className="ev-gallery-card"
              onClick={() => { pause(); setLightbox(i); }}
            >
              <img src={g.url} alt={g.event_type || "Sapore event"} loading="lazy" />
              {(g.event_type || g.location) && (
                <span className="ev-gallery-cap">
                  {g.event_type}{g.guests ? ` · ${g.guests} guests` : ""}{g.location ? ` · ${g.location}` : ""}
                </span>
              )}
            </button>
          ))}
        </div>
        <button className="ev-gallery-arrow right" onClick={() => scrollBy(1)} aria-label="Next" data-testid="ev-gallery-next"><ChevronRight size={20} /></button>
      </div>

      {lightbox !== null && visible[lightbox] && (
        <div className="ev-lightbox" onClick={() => setLightbox(null)} data-testid="ev-lightbox">
          <button className="ev-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close" data-testid="ev-lightbox-close"><X size={26} /></button>
          <button className="ev-lightbox-nav left" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + visible.length) % visible.length); }} aria-label="Previous"><ChevronLeft size={30} /></button>
          <figure className="ev-lightbox-fig" onClick={(e) => e.stopPropagation()}>
            <img src={visible[lightbox].url} alt={visible[lightbox].event_type || "Sapore event"} />
            {(visible[lightbox].event_type || visible[lightbox].location) && (
              <figcaption>{visible[lightbox].event_type}{visible[lightbox].guests ? ` · ${visible[lightbox].guests} guests` : ""}{visible[lightbox].location ? ` · ${visible[lightbox].location}` : ""}</figcaption>
            )}
          </figure>
          <button className="ev-lightbox-nav right" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % visible.length); }} aria-label="Next"><ChevronRight size={30} /></button>
        </div>
      )}
    </section>
  );
}
